import React, { useEffect } from 'react';
import { DisplayDataRow } from '../../Components/DisplayDataRow';
import { useNavigate } from 'react-router-dom';
import PageTitleAndSub from '../../Components/PageTitleAndSub';
import SelectedArtifactsTable from '../../Components/SelectedArtifactsTable';
import WorkflowFooter from '../../Components/workflow/WorkflowFooter';
import { PageContainer } from '../../StyledComponents/PageContainer';
import { TitleText } from '../../StyledComponents/Typography';
import { UserDeployInfoWorkflow } from './DeployPackageWorkflow';
import { StatusMessage } from '../../Components/StatusMessage';
import { scrollToTop } from '../../utils/Window';
import { Tag } from '../../Components/Tag';
import { useTheme } from '@canopysecurity/component-library';
import { useEnv } from '../../Context/EnvironmentProvider';
import { useMutation } from '@tanstack/react-query';
import {
  createDeployment,
  DeploymentInput,
} from '../../services/DeploymentService';
import { Artifact } from '../../services/ArtifactService';

export const FinalizeDeploy = ({
  userInput,
  goToNextStep,
  goToPreviousStep,
}: {
  userInput: UserDeployInfoWorkflow;
  goToNextStep: () => void;
  goToPreviousStep: () => void;
}): JSX.Element => {
  const { env } = useEnv();
  const navigate = useNavigate();
  const [error, setError] = React.useState<Error | undefined>(undefined);

  const {
    mutate,
    data,
    isPending: loading,
  } = useMutation({
    mutationFn: createDeployment,
    onError: (e) => {
      setError(e);
    },
  });

  useEffect(() => {
    scrollToTop();
  }, [data, error]);
  const deploymentData: DeploymentInput = {
    releaseId: userInput.release?.id ?? '',
    tagIds: userInput.tags?.map((t) => t.tagId).filter((t): t is string => !!t),
    forceInstall: userInput.forceInstall,
  };

  const { textStyles } = useTheme();
  const jobId = data?.jobId;
  return (
    <PageContainer>
      {error && <StatusMessage text={error.message} />}
      {jobId && <StatusMessage text={'Release successful. Job ID: ' + jobId} />}
      {data ? (
        <TitleText
          textStyle={textStyles.bodySemiBold}
          style={{
            textDecoration: 'underline',
            marginTop: '2%',
            marginBottom: '1%',
          }}
        >
          Details
        </TitleText>
      ) : (
        <PageTitleAndSub
          title={'Finalize & Deploy'}
          subtitle={
            'Review the following data and finalize the release for deployment.'
          }
        />
      )}
      <DisplayDataRow
        label={'Release Title'}
        data={userInput.release?.semver ?? undefined}
      />
      <DisplayDataRow
        label={'Device Type'}
        data={userInput.release?.deviceType ?? undefined}
      />
      <DisplayDataRow
        label={'Selected Artifacts'}
        data={
          <SelectedArtifactsTable
            selections={(userInput.release?.artifacts ?? []).reduce(
              (acc: Record<any, Artifact>, current) => {
                if (!current?.typeName) {
                  return acc;
                }
                return {
                  ...acc,
                  [current.typeName]: current,
                };
              },
              {},
            )}
          />
        }
      />
      <DisplayDataRow
        label={'Environment'}
        data={env.toUpperCase() ?? 'unknown'}
      />
      <DisplayDataRow
        label="Tags"
        data={userInput.tags.map((tag) => (
          <Tag text={tag.tagName ?? 'unknown'} key={tag.tagId ?? 'unknown'} />
        ))}
      />
      <DisplayDataRow
        label="Force Install"
        data={userInput.forceInstall ? 'True' : 'False'}
      />
      <WorkflowFooter
        primaryButton={{
          action: () => {
            if (data) {
              const deploymentId = data?.id;
              if (deploymentId) {
                navigate(`/details/${deploymentId}`);
              } else {
                goToNextStep();
              }
            } else {
              setError(undefined);
              mutate(deploymentData);
            }
          },
          name: data ? 'Close' : 'Deploy',
          disabled: false,
          loading,
        }}
        previousButton={{
          action: () => {
            goToPreviousStep();
          },
          visible: !data,
        }}
      />
    </PageContainer>
  );
};
